// src/services/timeline/timelineItemsProvider.js
// Azure Table Storage Provider für den INHALT eines Terminplans (Tabelle `timelineItems`).
//
// Single-Table-Design (wie projectCosts), differenziert über `Type` + RowKey-Präfix:
//   - settings           (Type=settings)  Ansicht/Zeitskala/Optionen + Projekt(e)-Meta
//   - owner_<id>          (Type=owner)     Zuständige (Name + Farbe)
//   - group_<id>          (Type=group)     Gruppen
//   - task_<id>           (Type=task)      Tasks / Meilensteine
//
// Das eingebettete Tool (public/timeline-embed.html) denkt in EINEM State-JSON
// (siehe normalizeState dort). Dieser Provider übersetzt:
//   - assemble(): Entities -> State-JSON (Laden)
//   - persistDiff(prev, next): nur geänderte/neue/gelöschte Entities schreiben (Speichern)
// Siehe ADR 0008.
import { ensureTableExists } from "../azureTableUtils";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "timelineItems",
  // Sicherheitshinweis: hartcodierter SAS-Token (rwdlacu) im Bundle – siehe ADR 0008.
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const DEFAULT_COLOR = "#1f2937";
const MS_PER_DAY = 86400000;

/* ---------- kleine Helfer ---------- */
function safeJsonParse(str, fallback) {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}
function toBool(v) {
  return v === true || v === "true";
}
function emptyToNull(v) {
  return v === "" || v === undefined ? null : v;
}
function pad2(n) {
  return ("0" + n).slice(-2);
}
function fmtISO(d) {
  return d.getUTCFullYear() + "-" + pad2(d.getUTCMonth() + 1) + "-" + pad2(d.getUTCDate());
}
function mondayOfCurrentWeekISO() {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const dn = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + (1 - dn));
  return fmtISO(d);
}
function addDaysISO(iso, days) {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, dd + days));
  return fmtISO(d);
}

/* ---------- State <-> Entity Mapping ---------- */
// Liefert die Default-State für einen brandneuen, leeren Terminplan.
function defaultState(fallbackName) {
  // crypto.randomUUID ist kollisionsfrei; der genaue Wert ist egal (interne Tool-ID).
  const projectId = `p_${crypto.randomUUID().slice(0, 8)}`;
  const viewStart = mondayOfCurrentWeekISO();
  const viewEnd = addDaysISO(viewStart, 13 * 7 - 1);
  return {
    projects: [{ id: projectId, name: fallbackName || "Terminplan", color: DEFAULT_COLOR }],
    owners: [],
    groups: [],
    tasks: [],
    timeScale: "week",
    viewStart,
    viewEnd,
    currentProjectId: projectId,
    ownerFiltersByProject: {},
    highlightByOwner: false,
    expandNotes: false,
    customColors: [],
  };
}

// Reine Datenfelder (ohne PartitionKey/RowKey/UpdatedAt) — Basis für Diff & Upsert.
function settingsData(state) {
  return {
    Type: "settings",
    TimeScale: ["day", "week", "month", "quarter", "year"].includes(state.timeScale) ? state.timeScale : "week",
    ViewStart: state.viewStart || "",
    ViewEnd: state.viewEnd || "",
    HighlightByOwner: !!state.highlightByOwner,
    ExpandNotes: !!state.expandNotes,
    Projects: JSON.stringify(state.projects || []),
    CurrentProjectId: state.currentProjectId || "",
    OwnerFilters: JSON.stringify(state.ownerFiltersByProject || {}),
    CustomColors: JSON.stringify(state.customColors || []),
  };
}
function ownerData(o) {
  return { Type: "owner", OwnerId: o.id, Name: o.name || "", Color: o.color || DEFAULT_COLOR };
}
function groupData(g) {
  return {
    Type: "group",
    GroupId: g.id,
    ProjectId: g.projectId || "",
    Name: g.name || "Gruppe",
    Collapsed: !!g.collapsed,
    GroupOrder: typeof g.order === "number" ? g.order : 0,
    OwnerId: g.ownerId || "",
    // Untergruppen + Gruppen-Abhängigkeiten (Major-Change Termine).
    ParentId: g.parentId || "",
    DependsOn: JSON.stringify(Array.isArray(g.dependsOn) ? g.dependsOn : []),
  };
}
function taskData(t) {
  return {
    Type: "task",
    TaskId: t.id,
    ProjectId: t.projectId || "",
    Name: t.name || "",
    DateMode: t.dateMode === "day" ? "day" : "week",
    StartWeek: typeof t.startWeek === "number" ? t.startWeek : 0,
    StartYear: typeof t.startYear === "number" ? t.startYear : 0,
    DurationWeeks: typeof t.durationWeeks === "number" ? t.durationWeeks : 1,
    StartDate: t.startDate || "",
    DurationDays: typeof t.durationDays === "number" ? t.durationDays : 7,
    IsMilestone: !!t.isMilestone,
    Done: !!t.done,
    Notes: t.notes || "",
    DependsOn: JSON.stringify(Array.isArray(t.dependsOn) ? t.dependsOn : []),
    GroupId: t.groupId || "",
    TaskOrder: typeof t.order === "number" ? t.order : 0,
    OwnerId: t.ownerId || "",
    Color: t.color || "",
    ManualPosition: typeof t.manualPosition === "boolean" ? t.manualPosition : true,
  };
}

// Entity -> State-Teil
function ownerFromEntity(e) {
  return { id: e.OwnerId, name: e.Name || "", color: e.Color || DEFAULT_COLOR };
}
function groupFromEntity(e) {
  return {
    id: e.GroupId,
    projectId: emptyToNull(e.ProjectId),
    name: e.Name || "Gruppe",
    collapsed: toBool(e.Collapsed),
    order: typeof e.GroupOrder === "number" ? e.GroupOrder : 0,
    ownerId: emptyToNull(e.OwnerId),
    parentId: emptyToNull(e.ParentId),
    dependsOn: safeJsonParse(e.DependsOn, []),
  };
}
function taskFromEntity(e) {
  return {
    id: e.TaskId,
    projectId: emptyToNull(e.ProjectId),
    name: e.Name || "",
    dateMode: e.DateMode === "day" ? "day" : "week",
    startWeek: typeof e.StartWeek === "number" ? e.StartWeek : Number(e.StartWeek) || 0,
    startYear: typeof e.StartYear === "number" ? e.StartYear : Number(e.StartYear) || 0,
    durationWeeks: typeof e.DurationWeeks === "number" ? e.DurationWeeks : Number(e.DurationWeeks) || 1,
    startDate: emptyToNull(e.StartDate),
    durationDays: typeof e.DurationDays === "number" ? e.DurationDays : Number(e.DurationDays) || 7,
    isMilestone: toBool(e.IsMilestone),
    done: toBool(e.Done),
    notes: e.Notes || "",
    dependsOn: safeJsonParse(e.DependsOn, []),
    groupId: emptyToNull(e.GroupId),
    order: typeof e.TaskOrder === "number" ? e.TaskOrder : Number(e.TaskOrder) || 0,
    ownerId: emptyToNull(e.OwnerId),
    color: emptyToNull(e.Color),
    manualPosition: toBool(e.ManualPosition),
  };
}

async function queryEntities(filter) {
  let all = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  do {
    let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;
    if (nextPartitionKey) url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
    if (nextRowKey) url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;

    const response = await fetch(url, { headers: { Accept: "application/json;odata=nometadata" } });
    if (!response.ok) {
      if (response.status === 404) break;
      throw new Error(`Azure query error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    all = all.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey || nextRowKey);
  return all;
}

export class TimelineItemsProvider {
  // Lädt alle Entities eines Plans und baut daraus das State-JSON für das Tool.
  // Für einen leeren Plan wird eine valide Default-State zurückgegeben.
  // Rückgabe: { state, hadSettings } — hadSettings=false signalisiert dem Aufrufer,
  // dass die settings-Entity (stabile Projekt-ID) noch angelegt werden muss.
  async assemble(timelineId, fallbackName) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const entities = await queryEntities(`$filter=PartitionKey eq '${timelineId}'`);

    if (entities.length === 0) return { state: defaultState(fallbackName), hadSettings: false };

    const state = defaultState(fallbackName);
    state.owners = [];
    state.groups = [];
    state.tasks = [];
    let hasSettings = false;

    for (const e of entities) {
      switch (e.Type) {
        case "settings":
          hasSettings = true;
          state.timeScale = ["day", "week", "month", "quarter", "year"].includes(e.TimeScale) ? e.TimeScale : "week";
          if (e.ViewStart) state.viewStart = e.ViewStart;
          if (e.ViewEnd) state.viewEnd = e.ViewEnd;
          state.highlightByOwner = toBool(e.HighlightByOwner);
          state.expandNotes = toBool(e.ExpandNotes);
          state.projects = safeJsonParse(e.Projects, state.projects);
          state.currentProjectId = e.CurrentProjectId || state.currentProjectId;
          state.ownerFiltersByProject = safeJsonParse(e.OwnerFilters, {});
          state.customColors = safeJsonParse(e.CustomColors, []);
          break;
        case "owner":
          state.owners.push(ownerFromEntity(e));
          break;
        case "group":
          state.groups.push(groupFromEntity(e));
          break;
        case "task":
          state.tasks.push(taskFromEntity(e));
          break;
        default:
          break;
      }
    }

    // Absicherung: currentProjectId muss auf ein existierendes Projekt zeigen.
    if (!state.projects.length) {
      const fresh = defaultState(fallbackName);
      state.projects = fresh.projects;
      state.currentProjectId = fresh.currentProjectId;
    } else if (!state.projects.find((p) => p.id === state.currentProjectId)) {
      state.currentProjectId = state.projects[0].id;
    }
    if (!hasSettings && !state.viewStart) {
      const fresh = defaultState(fallbackName);
      state.viewStart = fresh.viewStart;
      state.viewEnd = fresh.viewEnd;
    }
    return { state, hadSettings: hasSettings };
  }

  // Schreibt ausschliesslich die settings-Entity (für die einmalige Anlage bei neuen Plänen).
  async upsertSettings(timelineId, state) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    await this._executeOps(timelineId, [{ kind: "upsert", rowKey: "settings", data: settingsData(state) }]);
  }

  // Schreibt nur die Differenz zwischen prevState und nextState nach Azure.
  // Gibt die Anzahl geschriebener/gelöschter Entities zurück.
  async persistDiff(timelineId, prevState, nextState) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const ops = [];

    // Settings (immer genau eine Entity).
    const prevS = prevState ? settingsData(prevState) : null;
    const nextS = settingsData(nextState);
    if (!prevS || JSON.stringify(prevS) !== JSON.stringify(nextS)) {
      ops.push({ kind: "upsert", rowKey: "settings", data: nextS });
    }

    this._diffCollection(ops, "owner_", prevState?.owners, nextState.owners, (o) => o.id, ownerData);
    this._diffCollection(ops, "group_", prevState?.groups, nextState.groups, (g) => g.id, groupData);
    this._diffCollection(ops, "task_", prevState?.tasks, nextState.tasks, (t) => t.id, taskData);

    await this._executeOps(timelineId, ops);
    return ops.length;
  }

  // Hilfsfunktion: Upserts für neue/geänderte, Deletes für entfernte Elemente sammeln.
  _diffCollection(ops, prefix, prevArr, nextArr, getId, toData) {
    const prevById = new Map((prevArr || []).map((x) => [getId(x), x]));
    const nextById = new Map((nextArr || []).map((x) => [getId(x), x]));

    for (const item of nextArr || []) {
      const id = getId(item);
      const nextData = toData(item);
      const prevItem = prevById.get(id);
      if (!prevItem || JSON.stringify(toData(prevItem)) !== JSON.stringify(nextData)) {
        ops.push({ kind: "upsert", rowKey: `${prefix}${id}`, data: nextData });
      }
    }
    for (const id of prevById.keys()) {
      if (!nextById.has(id)) {
        ops.push({ kind: "delete", rowKey: `${prefix}${id}` });
      }
    }
  }

  async _executeOps(timelineId, ops) {
    const now = new Date().toISOString();
    const BATCH_SIZE = 10;
    const thunks = ops.map((op) => {
      if (op.kind === "delete") {
        return () => this._deleteEntity(timelineId, op.rowKey);
      }
      const entity = {
        PartitionKey: timelineId,
        RowKey: op.rowKey,
        ...op.data,
        UpdatedAt: now,
      };
      return () => this._upsertEntity(entity);
    });
    for (let i = 0; i < thunks.length; i += BATCH_SIZE) {
      const batch = thunks.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((fn) => fn()));
    }
  }

  async _upsertEntity(entity) {
    const pk = encodeURIComponent(entity.PartitionKey);
    const rk = encodeURIComponent(entity.RowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Accept: "application/json;odata=nometadata" },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure upsert error: ${response.status} - ${errorText}`);
    }
  }

  async _deleteEntity(timelineId, rowKey) {
    const pk = encodeURIComponent(timelineId);
    const rk = encodeURIComponent(rowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "DELETE", headers: { "If-Match": "*" } });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete error: ${response.status} ${response.statusText}`);
    }
  }

  // Kompletten Plan-Inhalt von einem Plan auf einen anderen kopieren (für Duplizieren).
  // Bei einem Fehler werden bereits kopierte Entities im Ziel wieder entfernt (Rollback).
  async copyAllItems(sourceTimelineId, targetTimelineId) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();
    const entities = await queryEntities(`$filter=PartitionKey eq '${sourceTimelineId}'`);

    const upserts = entities.map((e) => {
      const clone = { ...e };
      delete clone["odata.etag"];
      delete clone.Timestamp;
      clone.PartitionKey = targetTimelineId;
      clone.UpdatedAt = now;
      return clone;
    });

    const BATCH_SIZE = 10;
    const succeeded = [];
    try {
      for (let i = 0; i < upserts.length; i += BATCH_SIZE) {
        const batch = upserts.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((entity) => this._upsertEntity(entity)));
        succeeded.push(...batch);
      }
    } catch (err) {
      await Promise.allSettled(
        succeeded.map((entity) => this._deleteEntity(targetTimelineId, entity.RowKey))
      ).then((results) => {
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          console.error(`copyAllItems rollback: ${failed}/${succeeded.length} Cleanup-Löschungen fehlgeschlagen`);
        }
      });
      throw err;
    }
    return upserts.length;
  }
}
